/**
 * ProductGrid is a custom element that handles the display and lazy loading of products in a grid layout.
 * It manages product loading states, handles URL parameters, and implements infinite scrolling through
 * Intersection Observer.
 * @extends {window.BAO.CustomElement}
 */
class ProductGrid extends window.BAO.CustomElement() {
  /**
   * Creates an instance of ProductGrid.
   * Initializes state and event bus based on whether it's being used in category or search results context.
   */
  constructor () {
    super()

    this.state = {
      root: window.theme.category?.stateLayer?.state ||
        window.theme.searchResults?.stateLayer?.state || { products: [] },
      observer: null,
    }

    this.eventBus = window.theme.category
      ? window.theme.category.eventBus
      : window.theme.searchResults.eventBus

    this.productList = null
    this.productCache = new Map()
  }

  /**
   * Lifecycle callback when the element is added to the DOM.
   * Subscribes to product update events.
   */
  connectedCallback () {
    this.productList = this.querySelector('.prd-List_Items')
    this.subscribeToEvents()
  }

  /**
   * Lifecycle callback when the element is removed from the DOM.
   * Cleans up event listeners and disconnects the intersection observer.
   */
  disconnectedCallback () {
    if (this.observer) {
      this.observer.disconnect()
    }
    this.eventBus.unsubscribe('state:products:updated')
  }

  /**
   * Subscribes to product update events from the event bus.
   * Handles both empty product lists and product updates.
   */
  subscribeToEvents () {
    this.eventBus.subscribe('state:products:updated', products => {
      if (products.length === 0) {
        this.handleEmptyProducts()
        return
      }

      this.handleProductUpdate(products)
    })
  }

  /**
   * Handles the case when no products are found.
   * Displays a message and hides the product list.
   */
  handleEmptyProducts () {
    this.insertAdjacentHTML(
      'beforeend',
      `
      <p class="clc-ProductGrid_Text">
        ${window.theme.strings.collection.empty}
        <br>
        <facet-delete-all>
          <button type="button" class="clc-ProductGridFacetsTablet_Active clc-ProductGridFacetsTablet_ActiveLink">
            ${window.theme.strings.collection.filters.removeAppliedFilters}
          </button>
        </facet-delete-all>
      </p>
    `,
    )

    this.querySelector('.prd-List_Items').setAttribute('hidden', '')
  }

  /**
   * Handles product updates and orchestrates the update process.
   * @param {Array<Object>} products - Array of product objects to display
   */
  handleProductUpdate (products) {
    const urlParams = this.getUrlParameters()
    this.updateProductListVisibility()
    this.renderProductSkeletons(products)
    this.buildKlevuCards(products)

    if (this.hasUrlParameters(urlParams)) {
      this.handleUrlParameters(products, urlParams)
    } else {
      this.handleInitialLoad(products)
    }
  }

  /**
   * Extracts URL parameters from the current page URL.
   * @returns {Object} Object containing page, sortBy, and filters parameters
   */
  getUrlParameters () {
    const url = new URL(window.location.href)
    return {
      page: url.searchParams.get('page'),
      sortBy: url.searchParams.get('sort_by'),
      filters: url.searchParams.get('filters'),
    }
  }

  /**
   * Checks if any URL parameters are present.
   * @param {Object} params - URL parameters object
   * @returns {boolean} True if any parameters are present
   */
  hasUrlParameters (params) {
    return params.page || params.sortBy || params.filters
  }

  /**
   * Updates the visibility of the product list container.
   * Makes the list visible and clears its contents.
   */
  updateProductListVisibility () {
    const productList = this.querySelector('.prd-List_Items')
    productList.removeAttribute('hidden')
    productList.innerHTML = ''
  }

  /**
   * Renders skeleton loading placeholders for products.
   * @param {Array<Object>} products - Array of product objects
   */
  renderProductSkeletons (products) {
    const fragment = document.createDocumentFragment()

    products.forEach(product => {
      const li = document.createElement('li')

      if (
        window.location.hash &&
        window.location.hash.startsWith('#wishlist-')
      ) {
        li.setAttribute('id', `wishlist-${product.id}`)
      } else {
        li.setAttribute('id', `product-${product.id}`)
      }

      li.classList.add('prd-List_Item')
      li.setAttribute('data-product-grid-el', 'item')
      li.setAttribute('data-loading', 'true')
      li.innerHTML = this.createSkeletonHTML()

      fragment.appendChild(li)
    })

    this.productList.appendChild(fragment)
  }

  /**
   * Creates HTML for a product skeleton loading placeholder.
   * @param {Object} product - Product object containing at least an id
   * @returns {string} HTML string for the skeleton
   */
  createSkeletonHTML () {
    return `
      <div class="prd-CardSkeleton">
        <div class="prd-CardSkeleton_Image"></div>
        <div class="prd-CardSkeleton_Body"></div>
      </div>
    `
  }

  /**
   * Handles product updates when URL parameters are present.
   * @param {Array<Object>} products - Array of product objects
   * @param {Object} urlParams - URL parameters object
   */
  handleUrlParameters (products, urlParams) {
    if (this.observer) {
      this.observer.disconnect()
    }

    this.els.item.elements.forEach(item => {
      item.removeAttribute('data-url')
    })

    this.attachProductUrls(products)
    this.resetLoadingState()
    this.intersectionObserver()

    if (
      window.location.hash &&
      (window.location.hash.startsWith('#product-') ||
        window.location.hash.startsWith('#wishlist-'))
    ) {
      return
    }

    if (urlParams.page) {
      window.scrollTo({
        top: 0,
        behavior: 'instant',
      })
    }
  }

  /**
   * Handles initial product load without URL parameters.
   * @param {Array<Object>} products - Array of product objects
   */
  handleInitialLoad (products) {
    this.attachProductUrls(products)
    this.insertPromoItems()
    this.intersectionObserver()
  }

  /**
   * Resets the loading state for all product items.
   */
  resetLoadingState () {
    this.els.item.elements.forEach(item => {
      item.dataset.loading = true
    })
  }

  /**
   * Inserts promotional items into the product grid if available.
   */
  insertPromoItems () {
    const promoItems = document.querySelector('[data-promotion-items]')
    if (!promoItems) return

    this.els.item.elements.forEach((item, index) => {
      const promoItem = promoItems.querySelector(`[data-index="${index}"]`)

      if (promoItem) {
        item.insertAdjacentElement('beforebegin', promoItem)
      }
    })
  }

  /**
   * Sets up the Intersection Observer for lazy loading products.
   * Loads products when they come into view.
   */
  intersectionObserver () {
    const options = {
      root: null,
      rootMargin: '800px',
      threshold: 0,
    }

    this.observer = new IntersectionObserver(entries => {
      entries.forEach((entry, index) => {
        if (entry.isIntersecting) {
          if (entry.target.dataset.loading === 'true') {
            entry.target.dataset.loading = false

            this.fetchProductWithRetry(entry.target.dataset.url, index)
              .then(html => {
                entry.target.innerHTML = html
              })
              .catch(error => {
                console.error(
                  `Error fetching product ${entry.target.dataset.url}:`,
                  error,
                )
              })
          }
        }
      })
    }, options)

    this.els.item.elements.forEach(item => {
      this.observer.observe(item)
    })
  }

  /**
   * Builds and renders Klevu product cards for the first 8 products.
   * @param {Array<Object>} products - Array of product objects
   */
  buildKlevuCards (products) {
    const productsToRender = products.slice(0, 8)
    const items = this.els.item.elements

    if (products.length < items.length) {
      for (let i = products.length; i < items.length; i++) {
        items[i].style.display = 'none'
      }
    } else {
      items.forEach(item => {
        item.style.display = ''
      })
    }

    productsToRender.forEach((product, index) => {
      items[index].innerHTML = `
        <div class="prd-Card">
          <div class="prd-Card_MediaContainer">
            <img
              src="${product.image}"
              alt="${product.name}"
              style="aspect-ratio: 650 / 840; object-fit: cover;"
            >
          </div>
          <div class="prd-Card_Body">
            <div class="prd-Card_Title fz-15_18">
              ${product.name}
            </div>
            <div class="prd-Card_Price fz-15_35" style="height: 25px;">
            </div>
          </div>
        </div>
      `
    })
  }

  /**
   * Attaches product URLs to the grid items.
   * @param {Array<Object>} products - Array of product objects
   */
  attachProductUrls (products) {
    const items = this.els.item.elements

    this.state.root.products.forEach((product, index) => {
      const handle = product.url.split('/').pop()
      const url = `/products/${handle}`

      if (url) {
        items[index].setAttribute('data-url', url)
      } else {
        items[index].setAttribute('data-url', '')
      }
    })
  }

  async fetchProduct (url, index) {
    if (!url) {
      console.error('No URL provided for product fetch')
      return null
    }

    const currentPath = window.location.pathname
    const localeMatch = currentPath.match(/^\/([a-z]{2}-[a-z]{2})\//)

    if (
      localeMatch &&
      localeMatch[1] &&
      !url.startsWith(`/${localeMatch[1]}`)
    ) {
      const localePrefix = `/${localeMatch[1]}`
      url = `${localePrefix}${url}`
    }

    if (this.productCache.has(url)) {
      return this.productCache.get(url)
    }

    const section =
      this.dataset.bundleCollection === 'true'
        ? 'product-card-bundle'
        : 'product-card'

    const html = await window.BAO.sectionRenderer.fetchSingle(section, url)
    this.productCache.set(url, html)
    return html
  }

  async fetchProductWithRetry (url, index, retries = 3) {
    for (let i = 0; i < retries; i++) {
      try {
        return await this.fetchProduct(url, index)
      } catch (error) {
        if (i === retries - 1) throw error
        await new Promise(resolve => setTimeout(resolve, Math.pow(2, i) * 1000))
      }
    }
  }
}

customElements.define('product-grid', ProductGrid)
